import React, { useState, useEffect, useRef } from 'react'
import Head from 'next/head'
import Image from 'next/image'
import * as fbq from '@/services/fbpixel'
import allAlerts from '@/config/alerts.json'
import { v4 as uuidv4 } from 'uuid'
import PointingDownImage from 'public/pointing-down.png'
import ProductDisclosure from '@/components/Products/ProductFAQDisclosure'
import { GetStaticProps } from 'next'
import { ParsedUrlQuery } from 'querystring'
import { GetNotebookSlugs } from '@/services/api/products/GetSlugs'
import GetBySlug from '@/services/api/products/GetBySlug'
import { useRouter } from 'next/router'
import Link from 'next/link'
import toast from 'react-hot-toast'
import StarRatings from 'react-star-ratings'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  GetReviews,
  ReviewDelete,
  ReviewPost,
  ReviewUpdate,
} from '@/services/api/review'
import Button from '@/components/Button'
import useAuth from '@/hooks/useAuth'
import GetRelatedProducts from '@/services/api/products/GetRelatedProducts'
import ProductItem from '@/components/Products/ProductItem'
import {
  CheckInWishlist,
  GetWishlist,
  WishlistAdd,
  WishlistDelete,
} from '@/services/api/wishlist'
import { GetSettings } from '@/services/api/settings'
import {
  GetDocumentInputs,
  GetNotebookDocument,
} from '@/services/api/personalize/GetDocuments'
import language_translations, { LanguageTranslations } from '@/config/language'
import { useCartDispatch, useCartState } from '@/contexts/CartContext'
import { addItem, updateItem } from 'CartActions'
import MobileProductSection from '@/components/Product/MobileView'
import DesktopView from '@/components/Product/DesktopView'
import { useNotebookModal } from '@/contexts/NotebookModalContext'
import { useCTAContext } from '@/contexts/CTAContext'
import CustomToast from '@/components/CustomToast'
import { useLoginModal } from '@/contexts/LoginModalContext'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'
import useUserPreferences from '@/hooks/useUserPreferences'

interface IParams extends ParsedUrlQuery {
  slug: string
}

export const getStaticPaths = async () => {
  try {
    const arr: string[] = await GetNotebookSlugs()
    const newArr = Array.isArray(arr) ? arr : [];
    const paths = newArr.map(slug => ({
      params: { slug },
    }))
    return { paths, fallback: 'blocking' }
  } catch (error) {
    console.error('Error getting notebook slugs:', error)
    return { paths: [], fallback: 'blocking' }
  }
}

export const getStaticProps: GetStaticProps = async (context) => {
  const { slug } = context.params as IParams
  const locale = context.locale ?? 'en'
  
  // Use default ILS/IL for static generation
  const defaultCurrency = 'ILS'
  const defaultCountryCode = 'IL'
  
  try {
    const [staticProduct, settings, inputs] = await Promise.all([
      GetBySlug(slug, defaultCurrency, defaultCountryCode),
      GetSettings(defaultCountryCode, defaultCurrency), // Pass country first, then currency
      GetDocumentInputs(slug),
    ])
    
    return { 
      props: 
        { 
          ...(await serverSideTranslations(locale, [
            'products',
            'productsHeroBanner',
            'categoryTabs',
            'filterModal',
            'filterSidebar',
            'desktopHeader',
            'footer',
            'common',
            'account',
            'alerts',
            'address',
            'cart',
            'contactus',
            'deliveryPolicy',
            'notfound',
            'payment',
            'personalizeModal',
            'product',
            'removePhone'
          ])),
          staticProduct, 
          settings, 
          inputs,
          defaultCountryCode,
          defaultCurrency
        }, 
      revalidate: 10 
    }
  } catch (error) {
    console.error('Error fetching product data:', error)
    return { 
      props: 
        { 
          ...(await serverSideTranslations(locale, [
            'products',
            'productsHeroBanner',
            'categoryTabs',
            'filterModal',
            'filterSidebar',
            'desktopHeader',
            'footer',
            'common',
            'account',
            'alerts',
            'address',
            'cart',
            'contactus',
            'deliveryPolicy',
            'notfound',
            'payment',
            'personalizeModal',
            'product',
            'removePhone'
          ])),
          staticProduct: null, 
          settings: null, 
          inputs: null,
          defaultCountryCode: 'IL',
          defaultCurrency: 'ILS'
        }, 
      revalidate: 10 
    }
  }
}

interface Props {
  staticProduct: any
  settings: any
  inputs: any
  defaultCountryCode: string
  defaultCurrency: string
}

export default function Product({ 
  staticProduct, 
  settings: staticSettings, 
  inputs, 
  defaultCountryCode, 
  defaultCurrency 
}: Props) {
  // ALL React Hooks must be called at the TOP, before any conditional returns
  const router = useRouter()
  const preferences = useUserPreferences()
  const [product, setProduct] = useState(staticProduct)
  const [settings, setSettings] = useState(staticSettings)
  const [isRefetching, setIsRefetching] = useState(false)
  const { locale } = useRouter();
  const [inputName, setInputName] = useState(((locale === 'ar' ? staticProduct?.replace_name?.ar : staticProduct?.replace_name?.en)) || '')
  const [inputLanguage, setInputLanguage] = useState<keyof LanguageTranslations>('arabic')
  const [reviewStar, setReviewStar] = useState(0)
  const [review, setReview] = useState('')
  const [isEditing, setIsEditing] = useState<undefined | number>(undefined)
  const [wordCount, setWordCount] = useState(0)
  const [charCount, setCharCount] = useState(0)
  const [qty, setQty] = useState(1)
  
  // All other hooks must come here
  const { setIsOpen, setLocation } = useLoginModal()
  const { user } = useAuth('none')
  const cartDispatch = useCartDispatch()
  const cart = useCartState()
  const { t: tAlerts } = useTranslation('alerts')
  const alerts = allAlerts[locale as keyof typeof allAlerts] || allAlerts.ar;
  const { openModal } = useNotebookModal()
  const { setCTAVisible } = useCTAContext()
  const queryClient = useQueryClient()
  
  // Refs must also be at the top
  const ctaRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  
  // All useQuery hooks at the top
  const { data: reviews, isLoading: reviewsLoading } = useQuery(
    ['reviews', product?.id], 
    GetReviews, 
    { enabled: !!product?.id }
  )
  const { data: wishlist } = useQuery(['wishlist'], GetWishlist)
  const mutation = useMutation(ReviewPost)
  const DeleteMutation = useMutation(ReviewDelete)
  const UpdateMutation = useMutation(ReviewUpdate)
  const WishlistAddMutation = useMutation(WishlistAdd)
  const DeleteWishlistMutation = useMutation(WishlistDelete)
  
  const { data: document, isLoading: isDocumentLoading } = useQuery(
    [
      'personalize',
      {
        slug: product?.slug,
        name: inputName,
        language: inputLanguage,
      },
    ],
    GetNotebookDocument,
    { enabled: !!product?.slug }
  )
  
  const { data: related_products } = useQuery(
    [
      'related_products',
      {
        category_id: product?.categories?.map((cat: any) => cat['id']).toString() || '',
        product_id: product?.id,
        gender: '',
      },
    ],
    GetRelatedProducts,
    { enabled: !!product?.id }
  )

  const { data: check_in_wishlist } = useQuery(
    ['check_in_wishlist', product?.id],
    CheckInWishlist,
    { enabled: !!product?.id }
  )
  
  // Now set initial values from router query (but don't call hooks here)
  useEffect(() => {
    const nameFromQuery = router.query.name != undefined
      ? (router.query.name as string)
      : (locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en) || ''
    setInputName(nameFromQuery)
    
    const languageFromQuery = router.query.language != undefined
      ? (router.query.language as keyof LanguageTranslations)
      : 'arabic'
    setInputLanguage(languageFromQuery)
  }, [router.query, product?.replace_name.ar])

  // Refetch product and settings with user's currency preference
  useEffect(() => {
    const fetchDataWithUserPreferences = async () => {
      if (!preferences?.currencyCode || !staticProduct?.slug) return
      
      const userCurrency = preferences.currencyCode
      const userCountry = preferences.countryCode || 'IL'
      
      // Only refetch if preferences are different from static generation
      if (userCurrency !== defaultCurrency || userCountry !== defaultCountryCode) {
        setIsRefetching(true)
        try {
          const [updatedProduct, updatedSettings] = await Promise.all([
            GetBySlug(
              staticProduct.slug,
              userCurrency,
              userCountry
            ),
            GetSettings(userCountry, userCurrency) // Pass country first, then currency
          ])
          setProduct(updatedProduct)
          setSettings(updatedSettings)
        } catch (error) {
          console.error('Error fetching data with user preferences:', error)
          // Keep using static data if refetch fails
        } finally {
          setIsRefetching(false)
        }
      } else {
        // If user prefers same as static generation, use the static data
        setProduct(staticProduct)
        setSettings(staticSettings)
      }
    }
    
    fetchDataWithUserPreferences()
  }, [
    preferences?.currencyCode, 
    preferences?.countryCode, 
    staticProduct?.slug,
    staticSettings,
    defaultCountryCode,
    defaultCurrency
  ])

  React.useEffect(()=>{
    console.log('product:', product);
  },[]);

  // Other effects
  useEffect(() => {
    const words = review.split(' ')
    let wordCount = 0
    words.forEach((word: string) => {
      if (word.trim() !== '') {
        wordCount++
      }
    })
    setWordCount(wordCount)
    setCharCount(review.length)
  }, [review])

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setCTAVisible(entry.isIntersecting)
      },
      { threshold: 0.02 }
    )

    const currentElement = ctaRef.current
    if (currentElement) observer.observe(currentElement)

    return () => {
      if (currentElement) observer.unobserve(currentElement)
    }
  }, [setCTAVisible])

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0
    }
  }, [related_products])

  // Extract query values (not hooks, just variables)
  const gender = router.query.gender != undefined ? (router.query.gender as string) : ''
  const is_edit = router.query.is_edit != undefined ? (router.query.is_edit as string) : '0'
  const uuid = router.query.uuid !== undefined && router.query.uuid !== ''
    ? (router.query.uuid as string)
    : uuidv4()

  // Show loading while refetching - this must come AFTER all hooks
  if (isRefetching) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Updating prices...</p>
        </div>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Product Not Available</h1>
          <p>Sorry, this product is currently unavailable.</p>
          <Link
            href="/products"
            className="text-blue-500 hover:underline mt-4 inline-block"
          >
            Return to Products
          </Link>
        </div>
      </div>
    )
  }

  // Handler functions (these can come after conditionals since they're not hooks)
  const handleCreate = () => {
    mutation.mutate(
      { id: product.id, data: { stars: reviewStar, review } },
      {
        onSuccess() {
          queryClient.invalidateQueries(['reviews'])
          setReview('')
          setReviewStar(0)
          toast.success(alerts.review_added, { duration: 5000 })
        },
        onError(error: any) {
          toast.error(error.response.data.message, {
            duration: 4000,
            id: 'Validation Error',
          })
        },
      },
    )
  }

  const handleUpdate = () => {
    UpdateMutation.mutate(
      { id: isEditing, data: { stars: reviewStar, review } },
      {
        onSuccess() {
          queryClient.invalidateQueries(['reviews'])
          setReview('')
          setReviewStar(0)
          setIsEditing(undefined)
          toast.success(alerts.review_updated, { duration: 5000 })
        },
        onError(error: any) {
          toast.error(error.response.data.message, {
            duration: 4000,
            id: 'Validation Error',
          })
        },
      },
    )
  }
  
  const deleteReview = (id: number) => {
    DeleteMutation.mutate(id, {
      onSuccess() {
        queryClient.invalidateQueries(['reviews'])
      },
    })
  }
  
  const editReview = (id: number, stars: number, review: string) => {
    setReviewStar(stars)
    setReview(review)
    setIsEditing(id)
  }

  const addToWishlist = (id: number) => {
    if (!user) {
      toast.error(alerts.login_wishlist)
      return
    }
    WishlistAddMutation.mutate(id, {
      onSuccess() {
        queryClient.invalidateQueries(['wishlist'])
        queryClient.invalidateQueries(['check_in_wishlist'])
        toast.success(alerts.wishlist_add, { duration: 5000 })
      },
      onError(error: any) {
        toast.error(error.response.data.message, {
          duration: 4000,
          id: 'Validation Error',
        })
      },
    })
  }

  const WishlistDel = (id: number) => {
    DeleteWishlistMutation.mutate(id, {
      onSuccess() {
        queryClient.invalidateQueries(['wishlist'])
        queryClient.invalidateQueries(['check_in_wishlist'])
        toast.error(alerts.wishlist_remove, { duration: 5000 })
      },
      onError(error: any) {
        toast.error(error.response.data.message, {
          duration: 4000,
          id: 'Validation Error',
        })
      },
    })
  }

  const addToCart = () => {
    //Validation
    if (!user) {
      toast.custom((t) => <CustomToast type='error' t={t} message={tAlerts('login_first')} />, {
        duration: 2500,
      })
      setLocation('story')
      setIsOpen(true)
      return
    }
    
    const total_price = parseFloat(product.front_?.amount) * qty

    fbq.event('AddToCart', {
      content_name: product.product_name?.ar,
      currency: 'ILS',
      value: total_price,
    })
    
    const DemoName = locale === 'ar' ? product.demo_name?.ar : product.demo_name?.en
    const Name = locale === 'ar' ? product.product_name?.ar : product.product_name?.en;
    
    const item = {
      product_id: product.id,
      replace_name: inputName,
      inputs: { name: inputName },
      product_name: Name,
      demo_name: DemoName,
      default_replace_name: (locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en),
      image: product.images[0],
      product_slug: product.slug,
      price: parseFloat(product.front_price?.amount),
      quantity: qty,
      total: total_price,
      language: inputLanguage,
      cover_id: null,
      product_type: 2 as 2,
      discount: 0,
      uuid: uuid
    }

    if (is_edit === '1') {
      cartDispatch(updateItem(item))
      toast.success(alerts.cart_updated)
    } else {
      cartDispatch(addItem(item))
      toast.success(alerts.cart_added)
    }
  }
  
  const averageReviewStars = reviews?.reduce((acc:any, review:any) => acc + review.stars, 0) / reviews?.length
  
  const BuildInputValues = () => {
    const build_inputs: {[key: string]: string} = {}
    if (!inputs) return build_inputs
    
    inputs.forEach((input: string, index: number) => {
      if (input == 'name') {
        build_inputs[input] = inputName
      } else if (input === 'first_name') {
        build_inputs[input] = inputName.split(' ')[0]
      } else if (input === 'init') {
        build_inputs[input] = inputName.charAt(0)
      } else if (input === 'age') {
        build_inputs[input] = ''
      }
    })
    return build_inputs
  }
  
  const handlePersonalizeClick = () => {
    openModal({
      product: product,
      inputs: BuildInputValues()
    })
  }

  const DemoName = locale === 'ar' ? product.demo_name?.ar : product.demo_name?.en

  return (
    <>
      <Head>
        <title>
          {typeof DemoName === 'string' 
            ? DemoName.replace('{basmti}', inputName ?? (locale === 'ar' ? product?.replace_name?.ar : product?.replace_name?.en))
            : ''}
        </title>
        <meta name="description" content="Basmti Project" />
      </Head>
      <main className="bg-white pt-8 pb-24 border-t-[1px]">
        <MobileProductSection
          product={product}
          name={inputName}
          gender={gender}
          productGender={product?.has_female ? 'Female': 'Male'}
          averageReviewStars={averageReviewStars}
          check_in_wishlist={check_in_wishlist}
          WishlistDel={WishlistDel}
          addToWishlist={addToWishlist}
          handlePersonalizeClick={handlePersonalizeClick}
          settings={settings}
          reviews={reviews}
          user={user}
          reviewStar={reviewStar}
          setReviewStar={setReviewStar}
          review={review}
          setReview={setReview}
          wordCount={wordCount}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          handleCreate={handleCreate}
          handleUpdate={handleUpdate}
          mutation={mutation}
          UpdateMutation={UpdateMutation}
          DeleteMutation={DeleteMutation}
          editReview={editReview}
          deleteReview={deleteReview}
          related_products={related_products}
          scrollRef={scrollRef}
          ctaRef={ctaRef}
        />
        
        <DesktopView
          product={product}
          name={inputName}
          gender={gender}
          productGender={product?.has_female ? 'Female' : 'Male'}
          reviews={reviews}
          averageReviewStars={averageReviewStars}
          settings={settings}
          handlePersonalizeClick={handlePersonalizeClick}
          check_in_wishlist={check_in_wishlist}
          WishlistDel={WishlistDel}
          user={user}
          reviewStar={reviewStar}
          review={review}
          setReview={setReview}
          setReviewStar={setReviewStar}
          wordCount={wordCount}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          handleCreate={handleCreate}
          handleUpdate={handleUpdate}
          mutation={mutation}
          UpdateMutation={UpdateMutation}
          DeleteMutation={DeleteMutation}
          related_products={related_products}
          addToWishlist={addToWishlist}
          editReview={editReview}
          deleteReview={deleteReview}
          isLoading={reviewsLoading}
        />
      </main>
    </>
  )
}